/** Scatter tables derived from the content registry.
 *
 * The generator used to name every dwelling, bank and visitable in a hand list.
 * A new creature dwelling then shipped and never appeared on a random map until
 * someone remembered `catalog.ts`. The lists here are *computed*: every def of
 * the right kind is eligible unless it is in the explicit opt-out.
 *
 * Opt-outs are the objects a dedicated pass has to argue, not a second catalog:
 * a Oracle's Hut without a quest, a Mystery Casket without a guard, an obelisk
 * without a puzzle count, a town without a TownState.
 */

import type { ContentRegistry, CreatureDef, FactionId, MapObjectDef, TerrainKind } from "@game/core";
import type { Zone } from "./biomes.js";
import { HOME_BONUSES, SEER_HUTS, WATER_ONLY } from "./catalog.js";
import type { Rng } from "./rng.js";
import { pick, weighted } from "./rng.js";

export interface ScatterPool {
  readonly resources: readonly string[];
  readonly mines: readonly string[];
  readonly dwellings: readonly string[];
  readonly banks: readonly string[];
  readonly homeBonuses: readonly string[];
  readonly prizeBonuses: readonly string[];
  readonly shrines: readonly string[];
  readonly chests: readonly string[];
  readonly seaPickups: readonly string[];
  readonly seaBonuses: readonly string[];
  readonly boats: readonly string[];
  readonly undergroundMines: readonly string[];
  readonly undergroundDwellings: readonly string[];
  readonly undergroundBanks: readonly string[];
}

/** Kinds the scatter never places; each has a dedicated pass or is not content. */
const SKIP_KIND: ReadonlySet<string> = new Set([
  "town", "hero", "obstacle", "monster", "sign", "keymaster", "gate",
  "portal", "garrison", "scholar",
]);

const NEVER_SCATTER: ReadonlySet<string> = new Set([
  ...SEER_HUTS,
  "bld_mystery_casket",
  "bld_obelisk",
]);

const idsOf = (xs: readonly MapObjectDef[]): string[] =>
  xs.map((d) => d.id).sort();

const payloadStr = (d: MapObjectDef, k: string): string => {
  const v = d.payload[k];
  return typeof v === "string" ? v : "";
};

const isLand = (d: MapObjectDef): boolean => !WATER_ONLY.has(d.id);

function undergroundDwelling(content: ContentRegistry, d: MapObjectDef): boolean {
  const cr = payloadStr(d, "creature");
  if (cr === "") return false;
  const c = content.creatures.get(cr as CreatureDef["id"]);
  if (c === undefined) return false;
  for (const a of c.abilities) {
    if (a.kind === "UNDEAD" || a.kind === "NON_LIVING") return true;
  }
  const f = content.factions.get(c.faction);
  if (f === undefined) return false;
  if (f.nativeTerrain === "cavern" || f.nativeTerrain === "lava" || f.nativeTerrain === "dirt") {
    return true;
  }
  return false;
}

const poolCache = new WeakMap<ContentRegistry, ScatterPool>();

/** Every scatterable def, bucketed. Pure in the registry; cached per bundle. */
export function scatterPool(content: ContentRegistry): ScatterPool {
  const hit = poolCache.get(content);
  if (hit !== undefined) return hit;

  const all = [...content.mapObjects.values()]
    .filter((d) => !SKIP_KIND.has(d.kind) && !NEVER_SCATTER.has(d.id))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const homeSet = new Set(HOME_BONUSES);
  const resources: MapObjectDef[] = [];
  const mines: MapObjectDef[] = [];
  const dwellings: MapObjectDef[] = [];
  const banks: MapObjectDef[] = [];
  const homeBonuses: MapObjectDef[] = [];
  const prizeBonuses: MapObjectDef[] = [];
  const shrines: MapObjectDef[] = [];
  const chests: MapObjectDef[] = [];
  const seaPickups: MapObjectDef[] = [];
  const seaBonuses: MapObjectDef[] = [];
  const boats: MapObjectDef[] = [];

  for (const d of all) {
    const water = WATER_ONLY.has(d.id);
    switch (d.kind) {
      case "resource":
        if (water) seaPickups.push(d); else resources.push(d);
        break;
      case "mine":
        if (isLand(d)) mines.push(d);
        break;
      case "dwelling":
        if (isLand(d)) dwellings.push(d);
        break;
      case "bank":
        if (water) seaBonuses.push(d); else banks.push(d);
        break;
      case "shrine":
        if (isLand(d)) shrines.push(d);
        break;
      case "treasure":
        if (water) seaPickups.push(d); else chests.push(d);
        break;
      case "boat":
        boats.push(d);
        break;
      case "bonus":
        if (water) seaBonuses.push(d);
        else if (homeSet.has(d.id)) homeBonuses.push(d);
        else prizeBonuses.push(d);
        break;
      default:
        break;
    }
  }

  const undergroundMines = mines.filter((d) => payloadStr(d, "resource") !== "wood");
  const undergroundDwellings = dwellings.filter((d) => undergroundDwelling(content, d));
  const undergroundBanks = banks.slice();

  const pool: ScatterPool = Object.freeze({
    resources: idsOf(resources),
    mines: idsOf(mines),
    dwellings: idsOf(dwellings),
    banks: idsOf(banks),
    homeBonuses: idsOf(homeBonuses),
    prizeBonuses: idsOf(prizeBonuses),
    shrines: idsOf(shrines),
    chests: idsOf(chests),
    seaPickups: idsOf(seaPickups),
    seaBonuses: idsOf(seaBonuses),
    boats: idsOf(boats),
    undergroundMines: idsOf(undergroundMines),
    undergroundDwellings: idsOf(undergroundDwellings),
    undergroundBanks: idsOf(undergroundBanks),
  });
  poolCache.set(content, pool);
  return pool;
}

const creatureOf = (content: ContentRegistry, defId: string): CreatureDef | undefined => {
  const def = content.mapObjects.get(defId);
  const cr = def === undefined ? "" : payloadStr(def, "creature");
  if (cr === "") return undefined;
  return content.creatures.get(cr as CreatureDef["id"]);
};

/** Weight for a scatter pick: theme faction first, then native terrain. */
export function themedWeight(
  content: ContentRegistry, defId: string, theme: FactionId | null, terrain: TerrainKind,
): number {
  const c = creatureOf(content, defId);
  if (c === undefined) return 1;
  let w = 1;
  if (theme !== null && c.faction === theme) w += 8;
  const f = content.factions.get(c.faction);
  if (f === undefined) return w;
  if (f.nativeTerrain === terrain) w += 3;
  const extra = f.extraNative;
  if (extra !== undefined) {
    for (const t of extra) if (t === terrain) w += 3;
  }
  return w;
}

export function pickThemed(
  r: Rng, ids: readonly string[], content: ContentRegistry,
  theme: FactionId | null, terrain: TerrainKind,
): string | null {
  if (ids.length === 0) return null;
  return weighted(r, ids, (id) => themedWeight(content, id, theme, terrain));
}

const playableFactions = (content: ContentRegistry): readonly { id: FactionId; native: TerrainKind; extra: readonly TerrainKind[]; amphibious: boolean }[] =>
  [...content.factions.values()]
    .filter((f) => f.buildings.length > 0)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((f) => ({
      id: f.id,
      native: f.nativeTerrain,
      extra: f.extraNative ?? [],
      amphibious: f.amphibious === true || (f.id as string) === "saltreach" || (f.id as string) === "blackwater",
    }));

/** Dress each zone as a faction that belongs on its terrain. */
export function assignZoneThemes(
  zones: readonly Zone[], content: ContentRegistry,
  playerFactions: readonly FactionId[], r: Rng,
): void {
  const playable = playableFactions(content);
  for (const z of zones) {
    if (z.role === "player") {
      z.theme = playerFactions[z.player] ?? null;
      continue;
    }
    const natives = playable.filter((f) => {
      if (z.job === "water") return f.amphibious;
      if (f.native === z.terrain) return true;
      for (const t of f.extra) if (t === z.terrain) return true;
      return false;
    });
    const pool = natives.length > 0 ? natives : playable;
    const hit = pick(r, pool);
    z.theme = hit === null ? null : hit.id;
  }
}
